import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAuth } from 'firebase/auth';
import { Badge, Button, Message, Tag, Tooltip, Upload } from '@arco-design/web-react';
import { UploadItem } from '@arco-design/web-react/es/Upload';
import { IconImport, IconPlus } from '@arco-design/web-react/icon';
import { useQueryClient } from '@tanstack/react-query';

import { endpoints } from '../../../../api/endpoints';
import { RootState } from '../../../../store/store';
import { cachedToken } from '../../../../setupAxios';

import './ImportFile.css';
interface ImportResult {
  email: string;
  status: string;
}

const parseImportResponse = (report: string): ImportResult[] => {
  const cleaned = report.replace(/\\n/g, '\n');
  const lines = cleaned.split('\n').map(l => l.trim());

  return lines
    .filter(line => line.startsWith('Email:') || line.startsWith('Status:'))
    .reduce<ImportResult[]>((acc, line, index, arr) =>
      line.startsWith('Email:')
        ? [...acc, {
          email: line.replace('Email:', '').trim(),
          status: arr[index + 1]?.replace('Status:', '').trim() ?? ''
        }]
        : acc,
      []);
}

const getAmountOfImportedUsers = (imported: ImportResult[]): number => {
  const filtered = imported.filter(user => user.status === "Success" || user.status === "User already exists");

  return filtered.length;
} 

export const ImportFile = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const user = useSelector((state: RootState) => state.user.user);
  const currentUser = getAuth().currentUser;

  const [isFileUploading, setIsFileUploading] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<string | null>(null);

  const [importResults, setImportResults] = useState<ImportResult[]>([]);

  const [fileList, setFileList] = useState<UploadItem[]>([]);

  const uploadRef = React.useRef(null);

  const resetPrevUpload = () => {
    setFileList([]);
    setIsFileUploading(false);
  }

  useEffect(() => {
    if(fileList.length === 0) {
      setImportResults([]);
    }
  }, [fileList])

  useEffect(() => {
    const loadToken = async () => {
      try {
        const token = cachedToken ?? await currentUser?.getIdToken();

        if (token) {
          setIdToken(token);
        } else {
          console.warn("No token available for avatar upload");
        }
      } catch (error) {
        console.error("Failed to load token:", error);
      }
    };

    loadToken();
  }, [currentUser]);

  const renderResultsTooltip = () =>  (
    <div className='tooltip__wrapper'>
      <Tag
        size="small"
        color='green'
      >
        {getAmountOfImportedUsers(importResults)} / {importResults.length}
      </Tag>
      <span className='tooltip__content'>{t('admin.import_result')}</span>
    </div>
  );

  return (
    <div className='import-wrapper'>
      <Upload 
        ref={uploadRef} 
        accept={'.csv, .xlsx'}
        withCredentials={true}
        action={endpoints.admin.importUsers}
        headers={{Authorization: `Bearer ${idToken}`}}
        data={{ id: user?.id }}
        limit={1}
        showUploadList={{
          fileIcon: null,
          removeIcon: <IconPlus style={{display: isFileUploading ? 'none': 'block'}}/>,
          fileName: (file) => 
            <Tooltip className='import-tooltip' content={!isFileUploading &&renderResultsTooltip()} position="bottom">
              <Badge>
                <Tag color="green" style={{ cursor: 'pointer' }}>
                  {file.name}
                </Tag>
              </Badge>
            </Tooltip>, 
        }}
        
        onChange={(fileList, file) => {
          if (file.status === 'done') {
            setIsFileUploading(false);
    
            const responseText = JSON.stringify(file.response);
    
            if (responseText) {
              const parsedResults = parseImportResponse(responseText);
              setImportResults(parsedResults);
              setFileList(fileList);
              queryClient.invalidateQueries({ queryKey: ['profiles'] });
    
              if(fileList.length > 0) Message.success({
                content: t('notification_messages.file_uploaded_success'),
                closable: true,
                duration: 3000
              });
            } else {
              Message.error({
                content: t('notification_messages.file_uploaded_error'),
                duration: 3000
              });
              setFileList([]);
              setImportResults([]);
            }
          }
        }}
        onProgress={(e) => {
          if (e.status === 'uploading') {
            setIsFileUploading(true);
          }
        }}
        onRemove={() => {
          resetPrevUpload();
          setFileList([]);
        }}
      >
        <Button
          className="users-list__import-button"
          shape="round"
          size="small"
          disabled={isFileUploading}
        >
          {t('admin.import_button')} <IconImport />
        </Button>
        
      </Upload>


    </div>
  );
};